"use client";

import { useEffect, useState } from "react";
import { compareStreamUrl } from "@/lib/api";

/**
 * SSE hook for the /compare trade-off summary.
 *
 * Identical event contract to ``useAnalysisSummaryStream`` - token +
 * done - just pointed at the /api/compare/stream/{traceId} endpoint.
 * Kept as its own file so the two features evolve independently; if
 * the compare page ever needs different event names or framing (e.g.
 * structured "paragraph_break" markers), we won't be coupled.
 *
 * Pass ``null``/``undefined`` when there's nothing to stream (e.g.
 * the POST /api/compare returned trace_id=null because fewer than
 * two analyses succeeded). The hook no-ops and reports isDone=false
 * so the UI can hide the summary block entirely.
 */
export function useCompareSummaryStream(
  traceId: string | null | undefined,
): {
  streamingText: string;
  isStreaming: boolean;
  isDone: boolean;
  error: Error | null;
} {
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!traceId) {
      setStreamingText("");
      setIsStreaming(false);
      setIsDone(false);
      setError(null);
      return;
    }

    setStreamingText("");
    setIsStreaming(true);
    setIsDone(false);
    setError(null);

    const es = new EventSource(compareStreamUrl(traceId), { withCredentials: true });

    const handleToken = (e: MessageEvent) => {
      const tok = (e.data || "").replace(/\\n/g, "\n");
      if (tok) {
        setStreamingText((prev) => prev + tok);
      }
    };

    const handleDone = () => {
      setIsStreaming(false);
      setIsDone(true);
      es.close();
    };

    const handleError = () => {
      if (es.readyState === EventSource.CLOSED) {
        setIsStreaming(false);
        setIsDone(true);
        setError(new Error("SSE connection closed"));
        return;
      }
    };

    es.addEventListener("token", handleToken);
    es.addEventListener("done", handleDone);
    es.onerror = handleError;

    return () => {
      es.removeEventListener("token", handleToken);
      es.removeEventListener("done", handleDone);
      es.onerror = null;
      es.close();
    };
  }, [traceId]);

  return { streamingText, isStreaming, isDone, error };
}
